import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, Users, X, Hand, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { projectAPI, chatAPI, createQueryKey } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import GoalsModal from './GoalsModal';
import ParticipantsModal from './ParticipantsModal';
import QuickShoutModal from './QuickShoutModal';

// localStorage key for first-use pulse tracking
const FIRST_USE_PULSE_KEY = 'okio_statusbar_pulse_seen';

const ProjectStatusBar = memo(({ project, onFinish, isReadOnly = false, allowPointerPassthrough = false }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [isQuickShoutModalOpen, setIsQuickShoutModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // First-use pulse animation state
  const [showPulse, setShowPulse] = useState(false);
  const pulseTimeoutRef = useRef(null);
  
  // Check if current user is the project owner
  const isOwner = user && project && user.id === project.ownerId;

  // Handle window resize for responsive time format
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // First-use pulse animation: show for 3s on first project load, then dismiss
  useEffect(() => {
    if (!project?.id || !user?.id || project.status !== 'ongoing' || isReadOnly) return;
    
    // Check localStorage for this user
    const storageKey = `${FIRST_USE_PULSE_KEY}_${user.id}`;
    const hasSeenPulse = localStorage.getItem(storageKey);
    
    if (!hasSeenPulse) {
      // Show pulse animation
      setShowPulse(true);
      
      // Auto-dismiss after 3 seconds
      pulseTimeoutRef.current = setTimeout(() => {
        setShowPulse(false);
        localStorage.setItem(storageKey, 'true');
      }, 3000);
    }
    
    return () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, [project?.id, user?.id, project?.status, isReadOnly]);

  // Dismiss pulse on any button interaction
  const dismissPulse = () => {
    if (showPulse && user?.id) {
      setShowPulse(false);
      const storageKey = `${FIRST_USE_PULSE_KEY}_${user.id}`;
      localStorage.setItem(storageKey, 'true');
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    }
  };

  // Calculate objectives progress for Goals button visualization (simplified model)
  const objectivesProgress = useMemo(() => {
    if (!project?.objectives || !Array.isArray(project.objectives)) {
      return { completed: 0, total: 0, hasProgress: false, percentage: 0, progressText: "0/0" };
    }
    
    const objectives = project.objectives;
    const total = objectives.length;
    
    // Simplified model: just count objectives where isCompleted === true
    const completedObjectives = objectives.filter(obj => obj.isCompleted === true).length;
    
    return {
      completed: completedObjectives,
      total,
      hasProgress: total > 0,
      percentage: total > 0 ? Math.round((completedObjectives / total) * 100) : 0,
      progressText: `${completedObjectives}/${total}`
    };
  }, [project?.objectives]);

  // Simplified model: projects are always site-only
  const isSiteProject = true;
  
  // Fetch chats for Site projects to calculate accurate participant count
  // No polling needed - WebSocket events (participant_joined/participant_left) invalidate this query
  const { data: chatsResponse } = useQuery({
    queryKey: createQueryKey.chats(project?.id),
    queryFn: () => chatAPI.getByProject(project.id),
    enabled: !!project?.id && isSiteProject,
    staleTime: 0, // Always consider stale to ensure refetch on invalidation
    refetchOnMount: true, // Refetch when component mounts
  });

  // Participant count = the ROSTER: who is on this run (owner + everyone with
  // an active, unarchived chat). Deliberately NOT presence-based — phones lock
  // mid-walk and websockets blip, and the headline number must not flicker.
  // Who is reachable right now is a per-person dot in the ParticipantsModal.
  // The roster shrinks when someone actually leaves (their chat is archived).
  const participantsCount = useMemo(() => {
    if (!chatsResponse) {
      // While loading, use backend count as fallback
      return project?.participantCount ?? 0;
    }

    const chats = chatsResponse?.data?.data?.chats || [];
    const rosterIds = new Set(
      chats
        .filter(c => c.isActive === true && !c.archivedAt)
        .map(c => c.userId)
        .filter(Boolean)
    );
    // The owner is on their own run even before their chat exists
    if (project?.ownerId) {
      rosterIds.add(project.ownerId);
    }
    return rosterIds.size;
  }, [project?.participantCount, project?.ownerId, chatsResponse]);

  // Update project status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status }) => {
      return projectAPI.updateStatus(project.id, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      queryClient.invalidateQueries(['user-projects']); // Invalidate ProjectsPage queries
      queryClient.invalidateQueries(['homepage-games']); // Refresh homepage games list
      // Invalidate NewProjectPage queries to refresh /new page
      queryClient.invalidateQueries({ queryKey: ['new-page-live-projects'] });
      queryClient.invalidateQueries({ queryKey: ['new-page-ongoing-projects'] });
    },
    onError: (error) => {
      console.error('❌ Status update failed:', error);
      toast.error('Failed to update project status');
    }
  });

  // Simple timer based on created/updated timestamps (legacy scheduling removed)
  useEffect(() => {
    if (!project) return;

    const start = project.createdAt ? new Date(project.createdAt) : new Date();

    if (project.status === 'finished') {
      const end = project.updatedAt ? new Date(project.updatedAt) : new Date();
      const totalSeconds = Math.floor((end - start) / 1000);
      setElapsedSeconds(Math.max(0, totalSeconds));
      return;
    }

    if (project.status === 'ongoing') {
      const tick = () => {
        const now = new Date();
        const elapsed = Math.floor((now - start) / 1000);
        setElapsedSeconds(Math.max(0, elapsed));
      };
      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }

    setElapsedSeconds(0);
  }, [project]);

  // Format time display with mobile-optimized compact format
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      // Mobile: use colon format (3:19:40) - much more compact
      // Desktop: keep full format (3h 19m 40s) for clarity
      return isMobile 
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      // Mobile: use colon format (19:40) 
      // Desktop: keep full format (19m 40s)
      return isMobile
        ? `${minutes}:${secs.toString().padStart(2, '0')}`
        : `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // (removed) compact time helper was unused in the simplified status bar

  // Leave project mutation (for non-owners)
  const leaveProjectMutation = useMutation({
    mutationFn: () => projectAPI.leave(project.id),
    onSuccess: () => {
      toast.success('Left project');
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      queryClient.invalidateQueries(['participants', project.id]);
      queryClient.invalidateQueries(['project-chats', project.id]); // Invalidate chats (active chat was archived)
      queryClient.invalidateQueries(['chats', project.id]); // Invalidate chat queries
      // Navigate to explore page
      setTimeout(() => {
        window.location.href = '/starters';
      }, 500);
    },
    onError: (error) => {
      console.error('❌ Leave project failed:', error);
      toast.error('Failed to leave project');
    }
  });

  const navigate = useNavigate();

  // Archive session mutation (for Site projects)
  const archiveSessionMutation = useMutation({
    mutationFn: () => chatAPI.archiveCurrentSession(project.id),
    onSuccess: async () => {
      toast.success('Session ended');
      
      // Immediately update the cache to mark the active chat as archived
      // This provides instant UI feedback before refetch
      queryClient.setQueryData(['project-chats', project.id], (oldData) => {
        if (!oldData?.data?.data?.chats) return oldData;
        
        const updatedChats = oldData.data.data.chats.map(chat => {
          // Mark the active chat as archived immediately
          if (chat.isActive === true && chat.userId === user?.id) {
            return {
              ...chat,
              isActive: false,
              archivedAt: new Date().toISOString()
            };
          }
          return chat;
        });
        
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: {
              ...oldData.data.data,
              chats: updatedChats
            }
          }
        };
      });
      
      // Invalidate all related queries
      queryClient.invalidateQueries(['project-chats', project.id]);
      queryClient.invalidateQueries(['chats', project.id]);
      queryClient.invalidateQueries(createQueryKey.chats(project.id));
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      // Invalidate NewProjectPage queries to refresh /new page
      queryClient.invalidateQueries({ queryKey: ['new-page-live-projects'] });
      queryClient.invalidateQueries({ queryKey: ['new-page-ongoing-projects'] });
      
      // Force refetch ALL matching queries to ensure fresh data
      try {
        await queryClient.refetchQueries({
          queryKey: ['project-chats', project.id],
          type: 'all'
        });
      } catch (err) {
        console.warn('Refetch error (non-critical):', err);
      }
      
      // Navigate back to dashboard
      setTimeout(() => {
        navigate(`/projects/${project.id}`, { state: { sessionEnded: true } });
      }, 200);
    },
    onError: (error) => {
      console.error('Failed to archive session:', error);
      toast.error('Failed to end session');
    }
  });

  // Handle finish (with optional parent override for confirmation)
  const handleFinish = () => {
    // For Site projects: archive the current session instead of finishing the project
    if (isSiteProject) {
      if (typeof onFinish === 'function') {
        onFinish(); // Allow parent to show confirmation dialog
        return;
      }
      // Direct archive without confirmation
      archiveSessionMutation.mutate();
      return;
    }
    
    // Non-owners should leave, not finish
    if (!isOwner) {
      leaveProjectMutation.mutate();
      return;
    }
    
    if (typeof onFinish === 'function') {
      onFinish(); // Allow parent to show confirmation dialog
      return;
    }
    // Direct finish without confirmation
    updateStatusMutation.mutate({ status: 'finished' });
  };
  
  if (!project || project.status === 'pending') {
    return null; // Don't show while project is still pending
  }

  // Don't show "Completed" banner for Site projects - they're ongoing/utilitarian
  // Even if somehow marked as finished, Site projects should show the normal status bar
  if (project.status === 'finished' && !isSiteProject) {
    return (
      <div className="px-3 py-2.5">
        <div 
          className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-full"
          style={allowPointerPassthrough ? { pointerEvents: 'none' } : undefined}
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Completed</span>
          </div>
          <span className="text-xs text-emerald-600 dark:text-emerald-400 tabular-nums">
            {formatTime(elapsedSeconds)}
            </span>
        </div>
      </div>
    );
  }

  // Shared pill styles for visual consistency
  const pointerEventsClass = allowPointerPassthrough ? ' pointer-events-auto' : '';
  const basePillStyles = `inline-flex items-center gap-1.5 h-11 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.97]${pointerEventsClass}`;
  const defaultPillStyles = `${basePillStyles} bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-200`;
  const dangerPillStyles = `${basePillStyles} bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow hover:border-gray-300 dark:hover:border-gray-600 text-red-600 dark:text-red-400 focus-visible:ring-red-500`;

  // For ongoing or paused (though paused won't be used in UI anymore)
  const barPointerProps = allowPointerPassthrough ? { style: { pointerEvents: 'none' } } : {};

  return (
    <div className="p-1 w-full">
      <div className="flex items-center justify-between gap-2" {...barPointerProps}>
        
        {/* Stop/Leave Project button - changes to "Exit" for read-only mode */}
        <button
          onClick={handleFinish}
          disabled={!isReadOnly && (updateStatusMutation.isLoading || archiveSessionMutation.isLoading)}
          className={`${isReadOnly ? defaultPillStyles : dangerPillStyles} ${isMobile ? 'w-11 justify-center' : 'px-4'} disabled:opacity-50 disabled:cursor-not-allowed`}
          title={
            isReadOnly
              ? "Exit read-only view"
              : isSiteProject 
                ? "End current session" 
                : isOwner 
                  ? "Finish the project for everyone" 
                  : "Leave this project"
          }
        >
          {isReadOnly ? (
            <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          ) : (
            <X className="w-4 h-4" />
          )}
          <span className='hidden xl:inline'>
            {isReadOnly
              ? 'Exit'
              : isSiteProject 
                ? 'End' 
                : isOwner 
                  ? 'End' 
                  : 'Leave'}
          </span>
        </button>

        {/* Timer display */}
        {/* <div className="flex-1 flex items-center justify-center">
          {project?.status === 'ongoing' && (
            <div className={`inline-flex items-center gap-1.5 h-11 ${isMobile ? 'px-3.5' : 'px-4'} bg-white dark:bg-gray-900 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm`}>
              <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-900 dark:text-white font-semibold tabular-nums tracking-tight">{formatTime(elapsedSeconds)}</span>
            </div>
          )}
        </div> */}

        {/* Right side action buttons */}
        <div className="flex items-center gap-1.5">
        {/* Participants button */}
        {!isReadOnly && (
          <button
            onClick={() => { dismissPulse(); setIsParticipantsModalOpen(true); }}
            className={`${defaultPillStyles} ${isMobile ? (participantsCount > 1 ? 'px-2.5' : 'w-11 justify-center') : 'px-3.5'} focus-visible:ring-blue-500 ${showPulse ? 'animate-pulse-hint ring-2 ring-blue-400 ring-offset-1' : ''}`}
          >
              <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              {participantsCount > 1 ? (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[11px] font-bold">
                  {participantsCount}
                </span>
              ) : (
                <span className="hidden xl:inline text-gray-600 dark:text-gray-300">Invite</span>
              )}
          </button>
        )}

        {/* Quick Shout button */}
        {participantsCount > 1 && project.status === 'ongoing' && !isReadOnly && (
          <button
            onClick={() => setIsQuickShoutModalOpen(true)}
            className={`${defaultPillStyles} ${isMobile ? 'w-11 justify-center' : 'px-3.5'} hover:bg-purple-50 dark:hover:bg-purple-950/30 focus-visible:ring-purple-500`}
            title="Send a quick shout to all participants"
          >
              <Hand className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className='hidden xl:inline text-gray-600 dark:text-gray-300'>Shout</span>
          </button>
        )}

        {/* Goals button with progress visualization - hidden in read-only mode */}
        {!isReadOnly && (
        <button
          onClick={() => { dismissPulse(); setIsGoalsModalOpen(true); }}
          className={`${defaultPillStyles} ${isMobile ? (objectivesProgress.hasProgress ? 'px-2.5' : 'w-11 justify-center') : 'px-3.5'} focus-visible:ring-blue-500 ${showPulse ? 'animate-pulse-hint ring-2 ring-emerald-400 ring-offset-1' : ''}`}
        >
              <CheckCheck className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              {objectivesProgress.hasProgress ? (
                <span className="inline-flex items-center justify-center min-w-[32px] h-[18px] px-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold">
              {objectivesProgress.progressText}
            </span>
              ) : (
                <span className='hidden xl:inline text-gray-600 dark:text-gray-300'>Goals</span>
          )}
        </button>
        )}

        </div>
      </div>
      
      {/* Goals Modal */}
      <GoalsModal 
        isOpen={isGoalsModalOpen}
        onClose={() => setIsGoalsModalOpen(false)}
        project={project}
        currentUser={user}
      />

      {/* Participants Modal */}
      <ParticipantsModal
        isOpen={isParticipantsModalOpen}
        onClose={() => setIsParticipantsModalOpen(false)}
        projectId={project?.id}
        project={project}
      />

      {/* Quick Shout Modal */}
      {project?.status === 'ongoing' && (
        <QuickShoutModal
          isOpen={isQuickShoutModalOpen}
          projectId={project.id}
          onClose={() => setIsQuickShoutModalOpen(false)}
          onSendShout={async (message) => {
            await projectAPI.broadcastShout(project.id, message);
          }}
        />
      )}
    </div>
  );
});

ProjectStatusBar.displayName = 'ProjectStatusBar';

export default ProjectStatusBar;